/**
 * Admin user management — Super-admin only.
 *
 * Lists every user (customer / rider / store_admin / staff / ops) and lets
 * a super admin reset password, force logout, or enable/disable each one.
 * All three actions go through existing backend endpoints:
 *
 *   POST /admin/users/:id/reset-password    (new hash + token_epoch bump)
 *   POST /admin/users/:id/force-logout      (token_epoch bump only)
 *   PATCH /admin/users/:id/status           (is_disabled toggle)
 *
 * Passwords are never returned by the server. The reset dialog only shows
 * the NEW value the admin types — the previous password remains unknowable.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, LogOut, ShieldCheck, ShieldOff, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/features/auth/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type AdminUser = {
    id: string;
    email: string;
    full_name: string;
    phone: string;
    role: string;
    is_blocked: boolean;
    is_disabled?: boolean;
    store_id?: string | null;
    order_count: number;
};

async function fetchUsers(): Promise<AdminUser[]> {
    // Reuse the existing /admin/customers listing — it already covers every
    // non-legacy account. We display role/status so admins can act on any.
    const { data } = await api.get<{ items: AdminUser[] }>("/admin/customers?page_size=100");
    return data.items;
}

export default function AdminUserManagement() {
    const qc = useQueryClient();
    const [search, setSearch] = useState("");
    const [pwFor, setPwFor] = useState<AdminUser | null>(null);
    const [newPw, setNewPw] = useState("");

    const { data: users = [] } = useQuery({
        queryKey: ["admin", "users"],
        queryFn: fetchUsers,
    });

    const filtered = users.filter((u) => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return (
            u.email.toLowerCase().includes(q) ||
            u.full_name.toLowerCase().includes(q) ||
            u.role.toLowerCase().includes(q)
        );
    });

    const resetMut = useMutation({
        mutationFn: async ({ id, password }: { id: string; password: string }) => {
            await api.post(`/admin/users/${id}/reset-password`, { new_password: password });
        },
        onSuccess: () => {
            toast.success("Password reset — sessions invalidated");
            setPwFor(null);
            setNewPw("");
            qc.invalidateQueries({ queryKey: ["admin", "users"] });
        },
        onError: (e: unknown) => toast.error((e as Error).message || "Reset failed"),
    });

    const forceLogoutMut = useMutation({
        mutationFn: async (id: string) => api.post(`/admin/users/${id}/force-logout`),
        onSuccess: () => toast.success("Sessions terminated"),
        onError: (e: unknown) => toast.error((e as Error).message || "Force-logout failed"),
    });

    const statusMut = useMutation({
        mutationFn: async ({ id, disabled }: { id: string; disabled: boolean }) =>
            api.patch(`/admin/users/${id}/status`, { is_disabled: disabled }),
        onSuccess: () => {
            toast.success("Status updated");
            qc.invalidateQueries({ queryKey: ["admin", "users"] });
        },
        onError: (e: unknown) => toast.error((e as Error).message || "Update failed"),
    });

    const confirmReset = () => {
        if (newPw.length < 6) return toast.error("Password must be at least 6 characters");
        if (!pwFor) return;
        if (!window.confirm(`Reset password for ${pwFor.email}? All active sessions will end.`)) return;
        resetMut.mutate({ id: pwFor.id, password: newPw });
    };

    return (
        <div data-testid="admin-user-management" className="space-y-4">
            <header className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-brand" />
                    <h1 className="text-2xl font-extrabold">Users</h1>
                </div>
                <Input
                    placeholder="Search email, name or role"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="admin-users-search"
                    className="w-64"
                />
            </header>

            <div className="overflow-x-auto rounded-2xl border border-border/70 bg-card">
                <table className="min-w-full text-sm">
                    <thead className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        <tr>
                            <th className="px-4 py-3">Name</th>
                            <th className="px-4 py-3">Email</th>
                            <th className="px-4 py-3">Role</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Store</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((u) => (
                            <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-border/60">
                                <td className="px-4 py-3 font-semibold">{u.full_name || "—"}</td>
                                <td className="px-4 py-3">{u.email}</td>
                                <td className="px-4 py-3">
                                    <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold uppercase">
                                        {u.role}
                                    </span>
                                </td>
                                <td className="px-4 py-3">
                                    {u.is_disabled ? (
                                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-bold text-red-700">
                                            Disabled
                                        </span>
                                    ) : (
                                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700">
                                            Active
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                                    {u.store_id ? u.store_id.slice(0, 8) + "…" : "—"}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex justify-end gap-2">
                                        <button
                                            type="button"
                                            data-testid={`user-reset-${u.id}`}
                                            onClick={() => setPwFor(u)}
                                            className="inline-flex items-center gap-1 rounded-full border border-border/70 px-3 py-1 text-xs font-semibold hover:bg-muted"
                                        >
                                            <KeyRound className="h-3 w-3" /> Reset
                                        </button>
                                        <button
                                            type="button"
                                            data-testid={`user-force-logout-${u.id}`}
                                            onClick={() => {
                                                if (!window.confirm(`Force logout ${u.email}?`)) return;
                                                forceLogoutMut.mutate(u.id);
                                            }}
                                            className="inline-flex items-center gap-1 rounded-full border border-border/70 px-3 py-1 text-xs font-semibold hover:bg-muted"
                                        >
                                            <LogOut className="h-3 w-3" /> Force logout
                                        </button>
                                        <button
                                            type="button"
                                            data-testid={`user-toggle-${u.id}`}
                                            onClick={() => {
                                                const disabled = !u.is_disabled;
                                                if (
                                                    !window.confirm(
                                                        disabled
                                                            ? `Disable ${u.email}?`
                                                            : `Enable ${u.email}?`,
                                                    )
                                                )
                                                    return;
                                                statusMut.mutate({ id: u.id, disabled });
                                            }}
                                            className="inline-flex items-center gap-1 rounded-full border border-border/70 px-3 py-1 text-xs font-semibold hover:bg-muted"
                                        >
                                            {u.is_disabled ? (
                                                <>
                                                    <ShieldCheck className="h-3 w-3" /> Enable
                                                </>
                                            ) : (
                                                <>
                                                    <ShieldOff className="h-3 w-3" /> Disable
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {filtered.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                                    No users match your search.
                                </td>
                            </tr>
                        ) : null}
                    </tbody>
                </table>
            </div>

            {/* Reset password dialog */}
            {pwFor ? (
                <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
                    <div className="w-full max-w-md space-y-4 rounded-2xl border border-border bg-card p-5 shadow-elevated">
                        <div>
                            <h3 className="text-lg font-extrabold">Reset password</h3>
                            <p className="text-sm text-muted-foreground">
                                Set a temporary password for <strong>{pwFor.email}</strong>. Their
                                current password is never displayed — bcrypt is one-way. Existing
                                sessions will be invalidated immediately.
                            </p>
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="new-pw">New password</Label>
                            <Input
                                id="new-pw"
                                type="text"
                                value={newPw}
                                onChange={(e) => setNewPw(e.target.value)}
                                data-testid="admin-reset-password-input"
                                minLength={6}
                                autoFocus
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setPwFor(null);
                                    setNewPw("");
                                }}
                                className="rounded-full border border-border/70 px-4 py-1.5 text-sm font-semibold"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmReset}
                                disabled={resetMut.isPending}
                                data-testid="admin-reset-password-confirm"
                                className="rounded-full bg-brand px-4 py-1.5 text-sm font-bold text-white disabled:opacity-50"
                            >
                                Reset
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
